import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import type { Session } from '../types';

const formatStarted = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const adminErrorText = (err: unknown): string => (
  err instanceof Error ? err.message : 'Admin request failed'
);

interface AdminPanelProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideToggle?: boolean;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ open, onOpenChange, hideToggle = false }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [resetUsername, setResetUsername] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const panelOpen = open ?? internalOpen;

  const setPanelOpen = (nextOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
    } else {
      setInternalOpen(nextOpen);
    }
  };

  const refresh = async () => {
    setIsLoading(true);
    try {
      setSessions(await api.listAdminSessions());
      setStatus(null);
    } catch (err: unknown) {
      setStatus(adminErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!panelOpen) return undefined;
    refresh();
    const timer = window.setInterval(refresh, 10000);
    return () => window.clearInterval(timer);
  }, [panelOpen]);

  const resetMFA = async (username: string) => {
    const target = username.trim();
    if (!target) return;
    setIsLoading(true);
    try {
      await api.resetUserMFA(target);
      setStatus(`Second factor reset for ${target}`);
      setResetUsername('');
    } catch (err: unknown) {
      setStatus(adminErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const endSession = async (session: Session) => {
    if (!confirm(`End VDI session for ${session.user_id}?`)) return;
    setIsLoading(true);
    try {
      await api.destroyAdminSession(session.id);
      setStatus(`Ended VDI session for ${session.user_id}`);
      await refresh();
    } catch (err: unknown) {
      setStatus(adminErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`admin-panel-shell ${panelOpen ? 'open' : 'closed'} ${hideToggle ? 'admin-panel-no-toggle' : ''}`}>
      {!hideToggle && (
        <button type="button" className="admin-panel-toggle" onClick={() => setPanelOpen(!panelOpen)}>
          CUI Admin
        </button>
      )}
      {panelOpen && (
        <div className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2>VDI Admin</h2>
              <p>Running sessions and second-factor reset</p>
            </div>
            <button type="button" className="admin-panel-close" onClick={() => setPanelOpen(false)}>
              Close
            </button>
          </div>

          <div className="admin-reset-row">
            <input
              type="text"
              value={resetUsername}
              onChange={(e) => setResetUsername(e.target.value)}
              placeholder="username or email"
              disabled={isLoading}
            />
            <button
              type="button"
              disabled={isLoading || resetUsername.trim() === ''}
              onClick={() => resetMFA(resetUsername)}
            >
              Reset 2FA
            </button>
          </div>

          {status && <div className="admin-panel-status">{status}</div>}

          <div className="admin-session-table">
            <div className="admin-session-heading">
              <span>User</span>
              <span>Status</span>
              <span>Started</span>
              <span>Actions</span>
            </div>
            {sessions.length === 0 ? (
              <div className="admin-session-empty">
                {isLoading ? 'Loading sessions...' : 'No running VDI sessions'}
              </div>
            ) : sessions.map((session) => (
              <div className="admin-session-row" key={session.id}>
                <span>{session.user_id}</span>
                <span className={`admin-session-status ${session.status}`}>{session.status}</span>
                <span>{formatStarted(session.created_at)}</span>
                <span className="admin-session-actions">
                  <button type="button" disabled={isLoading} onClick={() => resetMFA(session.user_id)}>
                    Reset 2FA
                  </button>
                  <button type="button" disabled={isLoading} onClick={() => endSession(session)}>
                    End
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
